import React from 'react'
const { useState, useCallback, useMemo, useEffect, useRef } = React;

import {
  COMPANY_PROFILE, callOpenAI, channelIcon,
  MESSAGE_PROMPTS, resolvePromptTemplate,
  AUTH_TOKEN, AIRTABLE_BASE_ID, TABLE_IDS, AirtableAPI, CURRENT_USER,
} from '../globals.js';
import { F, linkedIds } from '../utils.jsx';
import MethodUsedNote from './MethodUsedNote.jsx';
import { EmptyState, ErrorState, LoadingState } from './ui/States.jsx';
import { searchContacts } from '../contactSearch.js';
import { outreachKind, countsAsInteraction } from '../outreachEvidence.js';
import { messageLabHistory } from '../commercialPrompts.js';

// ─── Constants ───────────────────────────────────────────────────────────────
// The keys are what the prompt and the logic read (unchanged); only the visible labels are in Spanish.
const CHANNELS   = ['WhatsApp', 'LinkedIn', 'Email'];
const MSG_TYPES  = ['First Touch', 'Follow-up', 'Breakup'];
const MSG_TYPE_KEY = { 'First Touch': 'first', 'Follow-up': 'followup', 'Breakup': 'breakup' };
const MSG_TYPE_LABEL = { 'First Touch': 'Primer contacto', 'Follow-up': 'Seguimiento', 'Breakup': 'Cierre de seguimiento' };
const LANGUAGES  = ['Auto (contact country)', 'English', 'Spanish', 'Portuguese', 'French', 'German', 'Italian'];
const LANGUAGE_LABEL = {
  'Auto (contact country)': 'Automático según el país del contacto', English: 'Inglés', Spanish: 'Español',
  Portuguese: 'Portugués', French: 'Francés', German: 'Alemán', Italian: 'Italiano',
};

// ─── Styles (the app's light theme tokens, src/styles/app.css) ──────────────
const CARD  = { background: 'var(--globant-card)', borderRadius: 12, border: '1px solid var(--globant-border)', padding: 16 };
const INPUT = { background: 'var(--globant-input)', border: '1px solid var(--globant-border)', borderRadius: 8, color: 'var(--globant-text)', padding: '7px 10px', fontSize: 13, fontFamily: 'inherit', outline: 'none', width: '100%', boxSizing: 'border-box' };
const SEL   = { ...INPUT, width: 'auto', minWidth: 140, maxWidth: '100%', cursor: 'pointer' };
const MUTED = { color: 'var(--globant-muted)', fontSize: 12 };
const GREEN = '#1E7B4F';
const CHIP  = { display: 'inline-flex', alignItems: 'center', gap: 4, background: 'var(--teal-dim)', border: '1px solid rgba(31,122,112,0.25)', borderRadius: 20, padding: '2px 10px', fontSize: 12, color: '#155C54' };
const FIELD_LABEL = { ...MUTED, display: 'block', fontWeight: 600, marginBottom: 4 };

// ─── Helpers ─────────────────────────────────────────────────────────────────
function getAccount(s, accounts) {
  const ids = linkedIds(s, 'Account');
  return ids.length ? accounts.find(a => a.id === ids[0]) || null : null;
}

function getOutreachFor(stkId, outreach) {
  return outreach
    .filter(o => (linkedIds(o, 'Stakeholder').includes(stkId) || linkedIds(o, 'Stakeholders').includes(stkId)))
    .sort((a, b) => (F(a, 'Date') || '').localeCompare(F(b, 'Date') || ''));
}

// Find best matching offering for an account
function getBestOffering(account, solutions) {
  if (!solutions.length) return null;
  // 1. Account directly linked to a solution
  const accSolIds = account ? linkedIds(account, 'Solutions') : [];
  if (accSolIds.length) {
    const sol = solutions.find(s => accSolIds.includes(s.id));
    if (sol) return sol;
  }
  // 2. Match by industry
  const industry = account ? F(account, 'Industry') : '';
  if (industry) {
    const byIndustry = solutions.find(s =>
      (F(s, 'Target Industry') || F(s, 'Industry') || '').toLowerCase().includes(industry.toLowerCase())
    );
    if (byIndustry) return byIndustry;
  }
  // 3. Fallback: first solution
  return solutions[0] || null;
}

// Build enriched prompt using Settings prompts + full context
function buildPrompt(s, account, outreach, campaigns, solutions, events, msgType, channel, language, opts = {}) {
  const cp = COMPANY_PROFILE || {};
  const name     = F(s, 'Name') || 'the contact';
  const aName    = account ? (F(account, 'Account Name') || '') : '';
  const role     = F(s, 'Title') || F(s, 'Role') || '';
  const industry = account ? (F(account, 'Industry') || '') : '';
  const country  = F(s, 'Country') || (account ? F(account, 'Country') : '') || '';
  const painPoints    = F(s, 'Pain Points (Generated)') || F(s, 'Pain Points') || '';
  const linkedinNews  = F(s, 'LinkedIn News (Generated)') || F(s, 'Linkedin latest news') || '';
  const accountNews   = account ? (F(account, 'Recent News') || '') : '';

  // Outreach history
  const hist = getOutreachFor(s.id, outreach);
  const touchCount = hist.length;
  const replies    = hist.filter(o => F(o, 'Direction') === 'Inbound' || F(o, 'Reply') === 'Yes' || F(o, 'Status') === 'Replied');
  const replyCount = replies.length;
  const replyState = replyCount > 0 ? `${replyCount} reply/replies received` : 'no replies yet';
  // Gmail subjects, synced fragments and bodies never go just because they are stored in Notes (src/outreachEvidence.js › aiTextOf)
  const histLines  = messageLabHistory(hist);

  // Settings prompt template
  const typeKey  = MSG_TYPE_KEY[msgType] || 'first';
  const template = (MESSAGE_PROMPTS || {})[typeKey] || '';
  const mission  = resolvePromptTemplate(template, { name, company: aName, touchCount, replyCount, replyState });

  // Offering context
  const offering = opts.offeringId
    ? solutions.find(s2 => s2.id === opts.offeringId)
    : getBestOffering(account, solutions);
  const offeringCtx = offering
    ? `Offering: ${F(offering, 'Name')}${F(offering, 'Service | Solution Detail') ? ` — ${F(offering, 'Service | Solution Detail').slice(0, 200)}` : ''}${F(offering, 'Stakeholder Key Message') ? `\nKey message: ${F(offering, 'Stakeholder Key Message').slice(0, 150)}` : ''}`
    : '';

  // Campaign context
  const campaign = opts.campaignId ? campaigns.find(c => c.id === opts.campaignId) : null;
  const campCtx  = campaign
    ? `Campaign: ${F(campaign, 'Name')} (${F(campaign, 'Type') || ''})\n${F(campaign, 'Message Template') ? `Angle: ${F(campaign, 'Message Template').slice(0, 200)}` : ''}${F(campaign, 'Context') ? `\nContext: ${F(campaign, 'Context').slice(0, 300)}` : ''}`
    : '';

  // Event context
  const event   = opts.eventId ? events.find(e => e.id === opts.eventId) : null;
  const eventCtx = event
    ? `Event to invite to: ${F(event, 'Name')} — ${F(event, 'Date') || ''} ${F(event, 'Location') || ''}\n${F(event, 'Description') ? F(event, 'Description').slice(0, 200) : ''}`
    : '';

  // Channel rules
  const channelRules = channel === 'Email'
    ? 'Format: first line must be "Subject: [subject]", blank line, then body. Max 4 sentences. Professional but warm. Follow the mission prompt structure exactly.'
    : 'Max 3 sentences. Concise, direct, conversational. No generic openers. No "Subject:" line.';

  // Language
  const langRule = (!language || language.startsWith('Auto'))
    ? `Use the language most appropriate for the contact${country ? ` (country: ${country})` : ''}.`
    : `Write ONLY in ${language}.`;

  return `You are ${cp.senderName || 'the sender'}, ${cp.senderTitle || 'Sales Rep'} at ${cp.companyName || 'our company'}.
IMPORTANT: Sign as "${cp.senderName || 'the sender'}" — NEVER use placeholders like [Your Name] or [Tu Nombre].
${cp.services ? `Services: ${cp.services}` : ''}
${cp.voiceTone ? `Tone/voice: ${cp.voiceTone}` : ''}

CONTACT: ${name}${role ? `, ${role}` : ''}${aName ? ` at ${aName}` : ''}${industry ? ` (${industry})` : ''}${country ? ` — ${country}` : ''}
${painPoints ? `Pain points: ${painPoints}` : ''}
${linkedinNews ? `LinkedIn activity: ${linkedinNews}` : ''}
${accountNews ? `Company news: ${accountNews}` : ''}

${offeringCtx ? `\n${offeringCtx}` : ''}
${campCtx ? `\n${campCtx}` : ''}
${eventCtx ? `\n${eventCtx}` : ''}

OUTREACH HISTORY (oldest → newest):
${histLines || 'No prior outreach — this is the first contact.'}

MISSION (follow this exactly): ${mission}

CHANNEL: ${channel}
${channelRules}
LANGUAGE: ${langRule}

BANNED PHRASES: "hope this finds you", "following up", "checking in", "touching base", "I wanted to reach out", "as per", "synergy".

Write ONE message only. No preamble, meta-commentary, or explanation. Just the message.`;
}

// ─── Gmail send ───────────────────────────────────────────────────────────────
async function sendEmailViaGmail(stakeholder, message) {
  const email = F(stakeholder, 'Email');
  if (!email) throw new Error('El contacto no tiene email cargado');
  const lines = message.split('\n');
  const si    = lines.findIndex(l => /^subject:/i.test(l.trim()));
  let subject = `Message for ${F(stakeholder, 'Name') || email}`;
  let body    = message;
  if (si !== -1) {
    subject = lines[si].replace(/^subject:\s*/i, '').trim();
    body    = lines.slice(si + 1).join('\n').trim();
  }
  const res = await fetch('/api/gmail/send', {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${AUTH_TOKEN}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({
      to: email, subject, message: body,
      stakeholderId: stakeholder.id,
      accountIds: linkedIds(stakeholder, 'Account'),
      baseId: AIRTABLE_BASE_ID,
      outreachTableId: TABLE_IDS.outreach,
    }),
  });
  if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.error || 'No se pudo enviar'); }
}

// ─── Small UI helpers ─────────────────────────────────────────────────────────
function CopyBtn({ text, label }) {
  const [copied, setCopied] = useState(false);
  return (
    <button className="action-btn" style={{ fontSize: 12, padding: '4px 10px' }}
      onClick={() => navigator.clipboard.writeText(text || '').then(() => { setCopied(true); setTimeout(() => setCopied(false), 1800); })}>
      {copied ? 'Copiado' : (label || 'Copiar')}
    </button>
  );
}

function StatusBadge({ status }) {
  const map = { pending: ['•', 'var(--globant-muted)', 'En espera'], generating: ['…', '#9A6212', 'Generando'], done: ['✓', GREEN, 'Listo'], error: ['✕', '#B4362C', 'Error'] };
  const [icon, color, label] = map[status] || ['?', 'var(--globant-muted)', ''];
  return <span style={{ fontSize: 12, color, fontWeight: 600, whiteSpace: 'nowrap' }}>{icon} {label}</span>;
}

function SendButtons({ stakeholder, message, onEmailSent, emailOnly }) {
  const phone   = F(stakeholder, 'Phone number');
  const linkedin = F(stakeholder, 'LinkedIn');
  const email   = F(stakeholder, 'Email');
  const msg     = message || '';
  const [emailStatus, setEmailStatus] = useState('idle');
  const [emailError,  setEmailError]  = useState('');
  const btn = { fontSize: 12, padding: '5px 12px' };

  async function handleSendEmail() {
    setEmailStatus('sending'); setEmailError('');
    try {
      await sendEmailViaGmail(stakeholder, msg);
      setEmailStatus('sent');
      if (onEmailSent) onEmailSent();
    } catch (e) { setEmailStatus('error'); setEmailError(e.message || 'Error'); }
  }

  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: emailOnly ? 0 : 8, alignItems: 'center' }}>
      {!emailOnly && phone && (
        <a href={`https://wa.me/${phone.replace(/\D/g, '')}?text=${encodeURIComponent(msg)}`}
          target="_blank" rel="noopener noreferrer" className="action-btn btn-whatsapp" style={btn}>
          💬 WhatsApp
        </a>
      )}
      {email && (
        <button className="action-btn btn-email" style={btn}
          onClick={handleSendEmail} disabled={emailStatus === 'sending' || emailStatus === 'sent'}>
          {emailStatus === 'sending' ? 'Enviando…' : emailStatus === 'sent' ? 'Enviado' : 'Enviar email'}
        </button>
      )}
      {!emailOnly && linkedin && (
        <a href={linkedin.startsWith('http') ? linkedin : `https://${linkedin}`}
          target="_blank" rel="noopener noreferrer" className="action-btn btn-linkedin" style={btn}>
          🔗 LinkedIn
        </a>
      )}
      {emailStatus === 'error' && <span style={{ fontSize: 11, color: '#B4362C' }}>{emailError}</span>}
      {!emailOnly && !phone && !email && !linkedin && <span style={MUTED}>Este contacto no tiene email, teléfono ni LinkedIn cargados.</span>}
    </div>
  );
}

// ─── Outreach History Panel ───────────────────────────────────────────────────
function HistoryPanel({ stakeholder, outreach }) {
  const [open, setOpen] = useState(false);
  const hist = getOutreachFor(stakeholder.id, outreach);
  if (!hist.length) return null;

  return (
    <div style={{ marginTop: 10 }}>
      <button type="button" className="action-btn btn-ghost" style={{ fontSize: 12, padding: '4px 10px' }} aria-expanded={open}
        onClick={() => setOpen(v => !v)}>
        {open ? 'Ocultar historial' : `Ver historial (${hist.length})`}
      </button>
      {open && (
        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {hist.slice(-8).reverse().map((o, i) => {
            const ch = F(o, 'Channel') || 'Sin canal';
            const inbound = outreachKind(o) === 'inbound';
            return (
              <div key={o.id || i} style={{ background: 'var(--globant-darker)', borderRadius: 8, padding: '8px 10px', borderLeft: `3px solid ${inbound ? GREEN : 'var(--globant-border)'}` }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 3, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--globant-text)' }}>{channelIcon[ch] || '📨'} {ch}</span>
                  <span style={MUTED}>{F(o, 'Date') || 'Sin fecha'}</span>
                  <span style={{ fontSize: 11, fontWeight: 600, color: inbound ? GREEN : 'var(--globant-muted)' }}>{interactionLabel(o)}</span>
                </div>
                <div style={{ fontSize: 13, color: 'var(--globant-text)', lineHeight: 1.5, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                  {(F(o, 'Message') || F(o, 'Notes') || '—').slice(0, 250)}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// What a history line is, in words (src/outreachEvidence.js decides the kind; opening a channel is not a contact).
function interactionLabel(o) {
  const k = outreachKind(o);
  return k === 'inbound' ? 'Respondió' : k === 'opened' ? 'Canal abierto (no cuenta como contacto)' : k === 'ignored' ? 'Borrador o pendiente' : 'Enviado';
}

// ─── Contact Picker ───────────────────────────────────────────────────────────
// The same contact search as ⌘K and Hoy (src/contactSearch.js): every loaded contact, with or without a company.
// The company only narrows the results; it is never required. Choosing only selects: nothing is written.
function ContactPicker({ stakeholders, accounts, onChange }) {
  const [q, setQ] = useState('');
  const [companyId, setCompanyId] = useState('');
  const searchId = React.useId();
  const companyFieldId = React.useId();

  const companies = useMemo(() => {
    const seen = new Set(); const list = [];
    stakeholders.forEach(s => linkedIds(s, 'Account').forEach(id => {
      const acc = accounts.find(a => a.id === id);
      if (acc && !seen.has(acc.id)) { seen.add(acc.id); list.push(acc); }
    }));
    return list.sort((a, b) => (F(a, 'Account Name') || '').localeCompare(F(b, 'Account Name') || '', 'es'));
  }, [stakeholders, accounts]);

  const typed = q.trim().length >= 2;
  const results = useMemo(() => {
    const ofCompany = (s) => !companyId || linkedIds(s, 'Account').includes(companyId);
    if (typed) return searchContacts(stakeholders, accounts, q, 1000).filter(h => ofCompany(h.record)).slice(0, 12);
    if (!companyId) return [];
    return stakeholders.filter(ofCompany)
      .map(s => ({ id: s.id, record: s, label: `${F(s, 'Name') || ''} ${F(s, 'Last name') || ''}`.trim() || F(s, 'Email') || '(sin nombre)', sub: [F(s, 'Role') || F(s, 'Title'), F(s, 'Email')].filter(Boolean).join(' · ') }))
      .sort((a, b) => a.label.localeCompare(b.label, 'es')).slice(0, 30);
  }, [q, typed, companyId, stakeholders, accounts]);

  return (
    <div className="card" style={{ padding: 16 }}>
      <div className="form-row" style={{ marginBottom: 6 }}>
        <div className="form-field">
          <label htmlFor={searchId}>Contacto</label>
          <input id={searchId} className="input-field" type="search" value={q} onChange={e => setQ(e.target.value)}
            placeholder="Buscar por nombre, email, rol o empresa…" autoComplete="off" />
        </div>
        <div className="form-field">
          <label htmlFor={companyFieldId}>Empresa <span className="form-optional">(opcional)</span></label>
          <select id={companyFieldId} className="input-field" value={companyId} onChange={e => setCompanyId(e.target.value)}>
            <option value="">Cualquier empresa o sin empresa</option>
            {companies.map(a => <option key={a.id} value={a.id}>{F(a, 'Account Name')}</option>)}
          </select>
        </div>
      </div>
      <div style={MUTED}>Podés elegir personas con o sin empresa. La empresa solo filtra la búsqueda.</div>

      {(typed || companyId) && (
        <div role="listbox" aria-label="Contactos encontrados" style={{ marginTop: 10, border: '1px solid var(--globant-border)', borderRadius: 10, overflow: 'hidden', maxHeight: 320, overflowY: 'auto' }}>
          {results.length === 0 && (
            <div style={{ ...MUTED, padding: 12 }}>
              {typed ? `Ningún contacto coincide con «${q.trim()}»${companyId ? ' en esa empresa' : ''}.` : 'Esa empresa no tiene contactos cargados.'}
            </div>
          )}
          {results.map((r, i) => {
            const acc = getAccount(r.record, accounts);
            return (
              <button key={r.id} type="button" role="option" aria-selected="false" onClick={() => { onChange(r.record); setQ(''); }}
                style={{ display: 'block', width: '100%', textAlign: 'left', background: 'var(--globant-card)', border: 'none', borderTop: i ? '1px solid var(--globant-border)' : 'none', padding: '10px 12px', cursor: 'pointer', fontFamily: 'inherit', color: 'var(--globant-text)' }}>
                <div style={{ fontSize: 14, fontWeight: 600 }}>{r.label}</div>
                <div style={{ ...MUTED, marginTop: 2 }}>{[F(r.record, 'Role') || F(r.record, 'Title'), acc ? F(acc, 'Account Name') : 'Sin empresa', F(r.record, 'Email')].filter(Boolean).join(' · ')}</div>
              </button>
            );
          })}
        </div>
      )}
      {!typed && q.trim().length === 1 && <div style={{ ...MUTED, marginTop: 8 }}>Escribí al menos 2 letras.</div>}
    </div>
  );
}

// ─── Chosen contact: brief context and the last interaction ──────────────────
function ContactContext({ stakeholder, account, outreach, onChange }) {
  const hist = getOutreachFor(stakeholder.id, outreach);
  const interactions = hist.filter(countsAsInteraction);
  const replies = hist.filter(o => outreachKind(o) === 'inbound');
  const last = interactions[interactions.length - 1] || null;
  const name = `${F(stakeholder, 'Name') || ''} ${F(stakeholder, 'Last name') || ''}`.trim() || F(stakeholder, 'Email') || '(sin nombre)';
  const role = F(stakeholder, 'Title') || F(stakeholder, 'Role') || '';
  const channels = [F(stakeholder, 'Email') && 'email', F(stakeholder, 'Phone number') && 'teléfono', F(stakeholder, 'LinkedIn') && 'LinkedIn'].filter(Boolean);
  const facts = [
    (F(stakeholder, 'Pain Points (Generated)') || F(stakeholder, 'Pain Points')) && 'Dolores identificados',
    F(stakeholder, 'LinkedIn News (Generated)') && 'Novedades de LinkedIn',
    account && F(account, 'Recent News') && 'Noticias de la empresa',
  ].filter(Boolean);

  return (
    <section className="card" aria-label="Contacto elegido" style={{ padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--globant-text)' }}>{name}</div>
          <div style={{ ...MUTED, fontSize: 13 }}>{[role, account ? F(account, 'Account Name') : 'Sin empresa', F(stakeholder, 'Status')].filter(Boolean).join(' · ')}</div>
        </div>
        <button type="button" className="action-btn btn-ghost" style={{ fontSize: 12, padding: '5px 12px' }} onClick={() => onChange(null)}>Cambiar contacto</button>
      </div>

      <div style={{ marginTop: 12, padding: '10px 12px', borderRadius: 10, background: 'var(--globant-darker)', border: '1px solid var(--globant-border)' }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--globant-muted)', marginBottom: 4 }}>Última interacción</div>
        {last ? (
          <>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--globant-text)' }}>
              {[F(last, 'Date') || 'Sin fecha', F(last, 'Channel'), interactionLabel(last)].filter(Boolean).join(' · ')}
            </div>
            <div style={{ fontSize: 13, color: 'var(--globant-text)', marginTop: 2, lineHeight: 1.5, overflowWrap: 'anywhere' }}>
              {(F(last, 'Message') || F(last, 'Notes') || '').slice(0, 160) || 'Sin texto registrado.'}
            </div>
          </>
        ) : <div style={{ fontSize: 13, color: 'var(--globant-text)' }}>Todavía no hay interacciones registradas: el mensaje será un primer contacto.</div>}
        <div style={{ ...MUTED, marginTop: 6 }}>
          {interactions.length} {interactions.length === 1 ? 'interacción' : 'interacciones'} · {replies.length} {replies.length === 1 ? 'respuesta' : 'respuestas'}
          {' · '}{channels.length ? `Canales: ${channels.join(', ')}` : 'Sin email, teléfono ni LinkedIn cargados'}
        </div>
      </div>

      {facts.length > 0 && (
        <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 6 }} aria-label="Datos que usa el mensaje">
          {facts.map(f => <span key={f} style={CHIP}>{f}</span>)}
        </div>
      )}
      <HistoryPanel stakeholder={stakeholder} outreach={outreach} />
    </section>
  );
}

// ─── Tab 1: Individual ────────────────────────────────────────────────────────
function IndividualTab({ data, preselectId, clearPreselectId }) {
  const { stakeholders = [], accounts = [], outreach = [], campaigns = [], solutions = [], events = [] } = data || {};
  const [selected,    setSelected]    = useState(null);
  const [msgType,     setMsgType]     = useState('First Touch');
  const [channel,     setChannel]     = useState('WhatsApp');
  const [language,    setLanguage]    = useState('Auto (contact country)');
  const [campaignId,  setCampaignId]  = useState('');
  const [offeringId,  setOfferingId]  = useState('');
  const [eventId,     setEventId]     = useState('');
  const [generatedMsg, setGeneratedMsg] = useState('');
  const [genError,    setGenError]    = useState('');
  const [methodUsed,  setMethodUsed]  = useState(null);
  const [generating,  setGenerating]  = useState(false);
  const ids = { type: React.useId(), channel: React.useId(), lang: React.useId(), offering: React.useId(), campaign: React.useId(), event: React.useId(), msg: React.useId(), why: React.useId() };

  const account  = selected ? getAccount(selected, accounts) : null;

  // Auto-select stakeholder when navigated from another view
  React.useEffect(() => {
    if (preselectId && stakeholders?.length) {
      const stk = stakeholders.find(s => s.id === preselectId);
      if (stk) choose(stk);
      if (clearPreselectId) clearPreselectId();
    }
  }, [preselectId, stakeholders]);

  // Auto-select best offering when contact selected
  React.useEffect(() => {
    if (selected && !offeringId) {
      const best = getBestOffering(account, solutions);
      if (best) setOfferingId(best.id);
    }
  }, [selected]);

  function choose(s) { setSelected(s); setGeneratedMsg(''); setGenError(''); setOfferingId(''); setMethodUsed(null); }

  async function handleGenerate() {
    if (!selected) return;
    setGenerating(true); setGeneratedMsg(''); setGenError('');
    try {
      const prompt = buildPrompt(selected, account, outreach, campaigns, solutions, events, msgType, channel, language, { campaignId, offeringId, eventId });
      const result = await callOpenAI({ prompt, max_tokens: 500, temperature: 0.75, method: 'messages', accountId: account?.id, onMethod: setMethodUsed });
      if (result) setGeneratedMsg(result);
      else setGenError('La IA no devolvió ningún texto. Volvé a intentarlo.');
    } catch (e) { setGenError(`No se pudo generar el mensaje: ${e.message || e}`); }
    setGenerating(false);
  }

  const why = generating ? 'Generando el mensaje…' : !selected ? 'Para generar, elegí primero un contacto.' : '';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {selected
        ? <ContactContext stakeholder={selected} account={account} outreach={outreach} onChange={choose} />
        : <ContactPicker stakeholders={stakeholders} accounts={accounts} onChange={choose} />}

      {!selected && (
        <EmptyState title="Elegí un contacto para preparar un mensaje">
          Buscá a la persona arriba. Vas a ver su contexto y su última interacción antes de generar; nada se envía hasta que elijas un canal.
        </EmptyState>
      )}

      <section className="card" aria-label="Opciones del mensaje" style={{ padding: 16 }}>
        <div className="form-row">
          <div className="form-field">
            <label htmlFor={ids.type}>Tipo de mensaje</label>
            <select id={ids.type} className="input-field" value={msgType} onChange={e => setMsgType(e.target.value)}>
              {MSG_TYPES.map(t => <option key={t} value={t}>{MSG_TYPE_LABEL[t]}</option>)}
            </select>
          </div>
          <div className="form-field">
            <label htmlFor={ids.channel}>Canal</label>
            <select id={ids.channel} className="input-field" value={channel} onChange={e => setChannel(e.target.value)}>
              {CHANNELS.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="form-field">
            <label htmlFor={ids.lang}>Idioma</label>
            <select id={ids.lang} className="input-field" value={language} onChange={e => setLanguage(e.target.value)}>
              {LANGUAGES.map(l => <option key={l} value={l}>{LANGUAGE_LABEL[l] || l}</option>)}
            </select>
          </div>
        </div>

        <details className="form-more">
          <summary>Más opciones: servicio, campaña y evento</summary>
          <div className="form-row" style={{ marginBottom: 0 }}>
            <div className="form-field">
              <label htmlFor={ids.offering}>Servicio</label>
              <select id={ids.offering} className="input-field" value={offeringId} onChange={e => setOfferingId(e.target.value)}>
                <option value="">Elegir según el contacto</option>
                {solutions.map(s => <option key={s.id} value={s.id}>{F(s, 'Name')}</option>)}
              </select>
            </div>
            <div className="form-field">
              <label htmlFor={ids.campaign}>Campaña <span className="form-optional">(opcional)</span></label>
              <select id={ids.campaign} className="input-field" value={campaignId} onChange={e => setCampaignId(e.target.value)}>
                <option value="">Ninguna</option>
                {campaigns.map(c => <option key={c.id} value={c.id}>{F(c, 'Name')}</option>)}
              </select>
            </div>
            <div className="form-field">
              <label htmlFor={ids.event}>Evento <span className="form-optional">(opcional)</span></label>
              <select id={ids.event} className="input-field" value={eventId} onChange={e => setEventId(e.target.value)}>
                <option value="">Ninguno</option>
                {events.map(e => <option key={e.id} value={e.id}>{F(e, 'Name') || F(e, 'Event Name')}</option>)}
              </select>
            </div>
          </div>
        </details>

        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginTop: 12 }}>
          <button type="button" className="action-btn btn-primary" onClick={handleGenerate}
            disabled={!selected || generating} aria-describedby={why ? ids.why : undefined}>
            {generating ? 'Generando…' : generatedMsg ? 'Volver a generar' : 'Generar mensaje'}
          </button>
          {why && <span id={ids.why} style={{ ...MUTED, fontSize: 13 }}>{why}</span>}
        </div>
      </section>

      {generating && <LoadingState text="Generando el mensaje con la IA…" />}
      {genError && !generating && <ErrorState message={genError} onRetry={selected ? handleGenerate : undefined} />}

      {generatedMsg && !generating && (
        <section className="card" aria-label="Mensaje generado" style={{ padding: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
            <label htmlFor={ids.msg} style={{ fontSize: 13, fontWeight: 700, color: 'var(--globant-text)' }}>Borrador · {MSG_TYPE_LABEL[msgType]} por {channel}</label>
            <CopyBtn text={generatedMsg} />
          </div>
          <textarea id={ids.msg} className="input-field" style={{ width: '100%', boxSizing: 'border-box', minHeight: 160, resize: 'vertical', lineHeight: 1.6, fontSize: 14, fontFamily: 'inherit' }}
            value={generatedMsg} onChange={e => setGeneratedMsg(e.target.value)} />
          <div style={{ ...MUTED, marginTop: 6 }}>Revisalo y editalo antes de enviarlo. Nada se envía solo: el email sale al tocar «Enviar email»; WhatsApp y LinkedIn se abren para que lo envíes vos.</div>
          <MethodUsedNote method={methodUsed} />
          {selected && <SendButtons stakeholder={selected} message={generatedMsg} />}
        </section>
      )}
    </div>
  );
}

// ─── Tab 2: Bulk ──────────────────────────────────────────────────────────────
function BulkTab({ data }) {
  const { stakeholders = [], accounts = [], outreach = [], campaigns = [], solutions = [], events = [] } = data || {};
  const [companyFilter,  setCompanyFilter]  = useState('');
  const [industryFilter, setIndustryFilter] = useState('');
  const [countryFilter,  setCountryFilter]  = useState('');
  const [statusFilter,   setStatusFilter]   = useState('');
  const [selected,    setSelected]    = useState(new Set());
  const [channel,     setChannel]     = useState('WhatsApp');
  const [language,    setLanguage]    = useState('Auto (contact country)');
  const [msgType,     setMsgType]     = useState('First Touch');
  const [campaignId,  setCampaignId]  = useState('');
  const [offeringId,  setOfferingId]  = useState('');
  const [eventId,     setEventId]     = useState('');
  const [bulkMsgs,    setBulkMsgs]    = useState({});
  const [generating,  setGenerating]  = useState(false);
  const [expanded,    setExpanded]    = useState(new Set());

  const uniqueAccounts = useMemo(() => {
    const seen = new Set(); const list = [];
    stakeholders.forEach(s => {
      const acc = getAccount(s, accounts);
      if (acc && !seen.has(acc.id)) { seen.add(acc.id); list.push(acc); }
    });
    return list.sort((a, b) => (F(a, 'Account Name') || '').localeCompare(F(b, 'Account Name') || ''));
  }, [stakeholders, accounts]);

  const uniqueIndustries = useMemo(() => { const s = new Set(); accounts.forEach(a => { const v = F(a, 'Industry'); if (v) s.add(v); }); return [...s].sort(); }, [accounts]);
  const uniqueCountries  = useMemo(() => { const s = new Set(); accounts.forEach(a => { const v = F(a, 'Country'); if (v) s.add(v); }); return [...s].sort(); }, [accounts]);
  const uniqueStatuses   = useMemo(() => { const s = new Set(); stakeholders.forEach(st => { const v = F(st, 'Status'); if (v) s.add(v); }); return [...s].sort(); }, [stakeholders]);

  const filtered = useMemo(() => stakeholders.filter(s => {
    const acc = getAccount(s, accounts);
    if (companyFilter  && (!acc || acc.id !== companyFilter)) return false;
    if (industryFilter && (!acc || F(acc, 'Industry') !== industryFilter)) return false;
    if (countryFilter  && (!acc || F(acc, 'Country') !== countryFilter)) return false;
    if (statusFilter   && F(s, 'Status') !== statusFilter) return false;
    return true;
  }), [stakeholders, accounts, companyFilter, industryFilter, countryFilter, statusFilter]);

  const allChecked = filtered.length > 0 && filtered.every(s => selected.has(s.id));
  function toggleAll() { setSelected(prev => { const n = new Set(prev); allChecked ? filtered.forEach(s => n.delete(s.id)) : filtered.forEach(s => n.add(s.id)); return n; }); }
  function toggleOne(id) { setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; }); }

  async function handleGenerateAll() {
    const targets = stakeholders.filter(s => selected.has(s.id));
    if (!targets.length) return;
    setGenerating(true);
    setBulkMsgs(prev => { const next = { ...prev }; targets.forEach(s => { next[s.id] = { text: '', status: 'pending' }; }); return next; });
    for (let i = 0; i < targets.length; i += 3) {
      const chunk = targets.slice(i, i + 3);
      setBulkMsgs(prev => { const next = { ...prev }; chunk.forEach(s => { next[s.id] = { ...next[s.id], status: 'generating' }; }); return next; });
      await Promise.all(chunk.map(async s => {
        const acc = getAccount(s, accounts);
        const effOfferingId = offeringId || (getBestOffering(acc, solutions)?.id || '');
        const prompt = buildPrompt(s, acc, outreach, campaigns, solutions, events, msgType, channel, language, { campaignId, offeringId: effOfferingId, eventId });
        try {
          const result = await callOpenAI({ prompt, max_tokens: 500, temperature: 0.75, method: 'messages', accountId: acc?.id });
          setBulkMsgs(prev => ({ ...prev, [s.id]: { text: result || '', status: 'done' } }));
        } catch (e) {
          setBulkMsgs(prev => ({ ...prev, [s.id]: { text: 'No se pudo generar: ' + (e.message || e), status: 'error' } }));
        }
      }));
    }
    setGenerating(false);
  }

  function handleCopyAll() {
    const lines = stakeholders.filter(s => selected.has(s.id) && bulkMsgs[s.id]?.status === 'done')
      .map(s => `=== ${F(s, 'Name')} ===\n${bulkMsgs[s.id].text}`).join('\n\n');
    navigator.clipboard.writeText(lines);
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <label><span style={FIELD_LABEL}>Empresa</span>
        <select style={SEL} value={companyFilter}  onChange={e => setCompanyFilter(e.target.value)}>
          <option value="">Todas las empresas</option>
          {uniqueAccounts.map(a => <option key={a.id} value={a.id}>{F(a, 'Account Name')}</option>)}
        </select></label>
        <label><span style={FIELD_LABEL}>Industria</span>
        <select style={SEL} value={industryFilter} onChange={e => setIndustryFilter(e.target.value)}>
          <option value="">Todas las industrias</option>
          {uniqueIndustries.map(i => <option key={i}>{i}</option>)}
        </select></label>
        <label><span style={FIELD_LABEL}>País</span>
        <select style={SEL} value={countryFilter}  onChange={e => setCountryFilter(e.target.value)}>
          <option value="">Todos los países</option>
          {uniqueCountries.map(c => <option key={c}>{c}</option>)}
        </select></label>
        <label><span style={FIELD_LABEL}>Estado</span>
        <select style={SEL} value={statusFilter}   onChange={e => setStatusFilter(e.target.value)}>
          <option value="">Todos los estados</option>
          {uniqueStatuses.map(s => <option key={s}>{s}</option>)}
        </select></label>
        <span style={{ ...MUTED, paddingBottom: 8 }}>{filtered.length} {filtered.length === 1 ? 'contacto' : 'contactos'}{filtered.length > 100 ? ' (se muestran los primeros 100)' : ''}</span>
      </div>

      {/* Contact table */}
      <div style={{ ...CARD, padding: 0, overflowX: 'auto' }}>
        <table style={{ width: '100%', minWidth: 560, borderCollapse: 'collapse', fontSize: 13, color: 'var(--globant-text)' }}>
          <thead>
            <tr style={{ background: 'rgba(30,42,44,0.04)', borderBottom: '1px solid var(--globant-border)' }}>
              <th style={{ padding: '8px 12px', width: 36 }}><input type="checkbox" aria-label="Elegir todos los contactos filtrados" checked={allChecked} onChange={toggleAll} /></th>
              <th style={{ padding: '8px 12px', textAlign: 'left' }}>Nombre</th>
              <th style={{ padding: '8px 12px', textAlign: 'left' }}>Empresa</th>
              <th style={{ padding: '8px 12px', textAlign: 'left' }}>Estado</th>
              <th style={{ padding: '8px 12px', textAlign: 'left' }}>Mensaje</th>
            </tr>
          </thead>
          <tbody>
            {filtered.slice(0, 100).map(s => {
              const acc       = getAccount(s, accounts);
              const isChecked = selected.has(s.id);
              const msgState  = bulkMsgs[s.id];
              const isExpanded = expanded.has(s.id);
              return (
                <React.Fragment key={s.id}>
                  <tr onClick={() => toggleOne(s.id)} style={{ borderBottom: '1px solid rgba(30,42,44,0.04)', background: isChecked ? 'var(--teal-dim)' : 'transparent', cursor: 'pointer' }}>
                    <td style={{ padding: '7px 12px' }}>
                      <input type="checkbox" aria-label={`Elegir a ${F(s, 'Name') || 'este contacto'}`} checked={isChecked} onChange={() => toggleOne(s.id)} onClick={e => e.stopPropagation()} />
                    </td>
                    <td style={{ padding: '7px 12px', fontWeight: 500 }}>{F(s, 'Name')}</td>
                    <td style={{ padding: '7px 12px', color: 'var(--globant-muted)' }}>{acc ? F(acc, 'Account Name') : '—'}</td>
                    <td style={{ padding: '7px 12px' }}>
                      <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, background: 'rgba(30,42,44,0.08)' }}>{F(s, 'Status') || '—'}</span>
                    </td>
                    <td style={{ padding: '7px 12px' }}>
                      {msgState ? (
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                          <StatusBadge status={msgState.status} />
                          {msgState.status === 'error' && <span style={{ fontSize: 12, color: '#B4362C' }}>{msgState.text}</span>}
                          {msgState.status === 'done' && (
                            <>
                              <button type="button" aria-expanded={isExpanded} style={{ background: 'none', border: 'none', color: 'var(--globant-green)', fontWeight: 600, cursor: 'pointer', fontSize: 12 }}
                                onClick={e => { e.stopPropagation(); setExpanded(prev => { const n = new Set(prev); n.has(s.id) ? n.delete(s.id) : n.add(s.id); return n; }); }}>
                                {isExpanded ? 'Ocultar' : 'Ver'}
                              </button>
                              <CopyBtn text={msgState.text} />
                            </>
                          )}
                        </div>
                      ) : <span style={MUTED}>—</span>}
                    </td>
                  </tr>
                  {isExpanded && msgState?.status === 'done' && (
                    <tr style={{ background: 'rgba(30,42,44,0.04)' }}>
                      <td />
                      <td colSpan={4} style={{ padding: '6px 12px 10px' }}>
                        <textarea style={{ ...INPUT, minHeight: 90, resize: 'vertical' }}
                          value={msgState.text}
                          onChange={e => setBulkMsgs(prev => ({ ...prev, [s.id]: { ...prev[s.id], text: e.target.value } }))} />
                        <SendButtons stakeholder={s} message={msgState.text} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={5} style={{ padding: 24, textAlign: 'center', color: 'var(--globant-muted)' }}>Ningún contacto coincide con los filtros</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Context + Generate controls */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        {selected.size > 0 && (
          <span style={{ ...CHIP, fontSize: 13, padding: '4px 12px', alignSelf: 'center' }}>{selected.size} elegidos</span>
        )}
        <div>
          <label><span style={FIELD_LABEL}>Tipo de mensaje</span>
          <select style={SEL} value={msgType} onChange={e => setMsgType(e.target.value)}>
            {MSG_TYPES.map(t => <option key={t} value={t}>{MSG_TYPE_LABEL[t]}</option>)}
          </select></label>
        </div>
        <div>
          <label><span style={FIELD_LABEL}>Canal</span>
          <select style={SEL} value={channel} onChange={e => setChannel(e.target.value)}>
            {CHANNELS.map(c => <option key={c} value={c}>{c}</option>)}
          </select></label>
        </div>
        <div>
          <label><span style={FIELD_LABEL}>Idioma</span>
          <select style={SEL} value={language} onChange={e => setLanguage(e.target.value)}>
            {LANGUAGES.map(l => <option key={l} value={l}>{LANGUAGE_LABEL[l] || l}</option>)}
          </select></label>
        </div>
        <div>
          <label><span style={FIELD_LABEL}>Servicio</span>
          <select style={SEL} value={offeringId} onChange={e => setOfferingId(e.target.value)}>
            <option value="">Según cada contacto</option>
            {solutions.map(s => <option key={s.id} value={s.id}>{F(s, 'Name')}</option>)}
          </select></label>
        </div>
        <div>
          <label><span style={FIELD_LABEL}>Campaña</span>
          <select style={SEL} value={campaignId} onChange={e => setCampaignId(e.target.value)}>
            <option value="">Ninguna</option>
            {campaigns.map(c => <option key={c.id} value={c.id}>{F(c, 'Name')}</option>)}
          </select></label>
        </div>
        <div>
          <label><span style={FIELD_LABEL}>Evento</span>
          <select style={SEL} value={eventId} onChange={e => setEventId(e.target.value)}>
            <option value="">Ninguno</option>
            {events.map(e => <option key={e.id} value={e.id}>{F(e, 'Name') || F(e, 'Event Name')}</option>)}
          </select></label>
        </div>
        <button className="action-btn btn-primary" onClick={handleGenerateAll}
          disabled={!selected.size || generating} style={{ opacity: (!selected.size || generating) ? 0.5 : 1, alignSelf: 'flex-end' }}>
          {generating ? 'Generando…' : `Generar (${selected.size || 0})`}
        </button>
        {!selected.size && !generating && <span style={{ ...MUTED, fontSize: 13, alignSelf: 'center' }}>Para generar, elegí al menos un contacto en la tabla.</span>}
        {Object.values(bulkMsgs).some(m => m.status === 'done') && (
          <button className="action-btn" onClick={handleCopyAll} style={{ fontSize: 12, alignSelf: 'flex-end' }}>Copiar todos</button>
        )}
      </div>
    </div>
  );
}

// ─── Sequences Tab ────────────────────────────────────────────────────────────
const SEQ_TIMEZONES = [
  { label: 'Buenos Aires (ART, UTC-3)', value: 'America/Argentina/Buenos_Aires' },
  { label: 'São Paulo (BRT, UTC-3)',    value: 'America/Sao_Paulo' },
  { label: 'Santiago (CLT)',            value: 'America/Santiago' },
  { label: 'Bogotá (COT, UTC-5)',       value: 'America/Bogota' },
  { label: 'México DF (CST, UTC-6)',    value: 'America/Mexico_City' },
  { label: 'New York (ET)',             value: 'America/New_York' },
  { label: 'London (GMT/BST)',          value: 'Europe/London' },
  { label: 'Madrid (CET)',              value: 'Europe/Madrid' },
  { label: 'UTC',                       value: 'UTC' },
];

const SEQ_STATUS_META = {
  active:   { label: 'Activa',   color: '#1E7B4F', bg: 'rgba(74,222,128,0.12)',  border: 'rgba(74,222,128,0.3)'  },
  paused:   { label: 'En pausa',   color: '#9A6212', bg: 'rgba(251,191,36,0.12)', border: 'rgba(251,191,36,0.3)'  },
  finished: { label: 'Terminada', color: 'var(--globant-muted)', bg: 'rgba(30,42,44,0.05)', border: 'var(--globant-border)' },
};

function newSeqId() { return 'seq_' + Date.now(); }

function defaultSeq(name = 'Nueva secuencia') {
  return {
    id: newSeqId(), name,
    status: 'active',
    language: 'es',
    steps: [
      { channel: 'Email', waitDays: 0,  condition: 'always',   note: '' },
      { channel: 'Email', waitDays: 3,  condition: 'no_reply', note: '' },
      { channel: 'Email', waitDays: 7,  condition: 'no_reply', note: '' },
    ],
    config: { sendMode: 'send', sendOnEnroll: true, sendHour: 9, timezone: 'America/Argentina/Buenos_Aires' },
    enrollments: {},
  };
}

function SequencesTab({ data, api, onLogActivity, onUpdateRecord }) {
  const { stakeholders = [], accounts = [], campaigns = [], events = [], solutions = [], outreach = [] } = data || {};

  // ── Context ──
  const [ctxType,    setCtxType]    = useState('campaign');
  const [campaignId, setCampaignId] = useState('');
  const [eventId,    setEventId]    = useState('');
  const [serviceId,  setServiceId]  = useState('');

  // ── Sequences list ──
  const [sequences,    setSequences]    = useState([]);  // loaded from Airtable
  const [activeSeqId,  setActiveSeqId]  = useState(null);
  const [saving,       setSaving]       = useState(false);
  const [renamingId,   setRenamingId]   = useState(null);
  const [renameVal,    setRenameVal]    = useState('');

  // ── Per-sequence editor state ──
  const [accFilter,        setAccFilter]        = useState('');
  const [indFilter,        setIndFilter]        = useState('');
  const [roleFilter,       setRoleFilter]       = useState('');
  const [selected,         setSelected]         = useState(new Set());
  const [enrollDT,         setEnrollDT]         = useState('');
  const [expandedLog,      setExpandedLog]      = useState(new Set());
  const [enrolling,        setEnrolling]        = useState(false);
  const [running,          setRunning]          = useState(false);
  const [stepPreviews,     setStepPreviews]     = useState({});
  const [generatingPreview,setGeneratingPreview]= useState(null);

  // ── Derived ──
  const selectedCampaign = campaigns.find(c => c.id === campaignId) || null;

  // ── Sequence analytics (cross all sequences of this campaign) ──
  const seqAnalytics = useMemo(() => {
    if (!sequences.length) return null;
    const stats = sequences.map(seq => {
      const enrVals = Object.values(seq.enrollments || {});
      const totalEnrolled = enrVals.length;
      const activeCount   = enrVals.filter(e => e.status === 'active').length;
      const doneCount     = enrVals.filter(e => ['completed','replied'].includes(e.status)).length;
      const repliedCount  = enrVals.filter(e => e.status === 'replied').length;
      const dueCount      = enrVals.filter(e => e.status === 'active' && new Date(e.nextDateTime || e.nextDate || 0) <= new Date()).length;
      // Count sent emails from outreach for enrolled contacts in this sequence
      const enrolledIds = Object.keys(seq.enrollments || {});
      const seqOutreach = outreach.filter(o => {
        const stkIds = linkedIds(o,'Stakeholder');
        return stkIds.some(id => enrolledIds.includes(id)) && F(o,'Status') !== 'Draft';
      });
      const sentCount    = seqOutreach.length;
      const repliedOut   = seqOutreach.filter(o => ['received','replied'].includes(F(o,'Status').toLowerCase())).length;
      const replyRate    = sentCount > 0 ? Math.round(repliedOut / sentCount * 100) : 0;
      return { seq, totalEnrolled, activeCount, doneCount, repliedCount, dueCount, sentCount, repliedOut, replyRate };
    });
    const total = {
      sequences: stats.length,
      enrolled:  stats.reduce((s,r) => s + r.totalEnrolled, 0),
      sent:      stats.reduce((s,r) => s + r.sentCount, 0),
      replied:   stats.reduce((s,r) => s + r.repliedOut, 0),
      due:       stats.reduce((s,r) => s + r.dueCount, 0),
    };
    total.replyRate = total.sent > 0 ? Math.round(total.replied / total.sent * 100) : 0;
    return { stats, total };
  }, [sequences, outreach]);

  // Load sequences from campaign when campaign changes
  useEffect(() => {
    if (!selectedCampaign) { setSequences([]); setActiveSeqId(null); return; }
    try {
      const loaded = JSON.parse(F(selectedCampaign, 'Campaign Sequences') || '[]');
      setSequences(Array.isArray(loaded) ? loaded : []);
    } catch { setSequences([]); }
    setActiveSeqId(null);
    setSelected(new Set());
    setStepPreviews({});
  }, [campaignId]);

  const activeSeq = sequences.find(s => s.id === activeSeqId) || null;

  // ── Persist sequences to Airtable ──
  const saveSequences = async (newSeqs) => {
    if (!selectedCampaign) return;
    setSaving(true);
    const json = JSON.stringify(newSeqs);
    try {
      const a = api || new AirtableAPI();
      await a.updateRecord(TABLE_IDS.campaigns, selectedCampaign.id, { 'Campaign Sequences': json });
      if (onUpdateRecord) onUpdateRecord('campaigns', selectedCampaign.id, { 'Campaign Sequences': json });
    } catch(e) { window.__oikeToast?.('No se pudo guardar: ' + e.message, 'error'); }
    setSaving(false);
  };

  const updateActiveSeq = (patch) => {
    const newSeqs = sequences.map(s => s.id === activeSeqId ? { ...s, ...patch } : s);
    setSequences(newSeqs);
    return newSeqs;
  };

  // ── Contact helpers ──
  const ctxRecord = ctxType === 'campaign' ? selectedCampaign
    : ctxType === 'event' ? events.find(e => e.id === eventId)
    : solutions.find(s => s.id === serviceId);

  const industries = useMemo(() => [...new Set(accounts.map(a => F(a,'Industry')).filter(Boolean))].sort(), [accounts]);
  const roles = useMemo(() => [...new Set(stakeholders.map(s => F(s,'Role')).filter(Boolean))].sort(), [stakeholders]);
  const uniqueAccounts = useMemo(() => {
    const seen = new Set(); const list = [];
    stakeholders.forEach(s => { const acc = accounts.find(a => linkedIds(s,'Account').includes(a.id)); if (acc && !seen.has(acc.id)) { seen.add(acc.id); list.push(acc); } });
    return list.sort((a,b) => (F(a,'Account Name')||'').localeCompare(F(b,'Account Name')||''));
  }, [stakeholders, accounts]);

  const filtered = useMemo(() => stakeholders.filter(s => {
    if (!F(s,'Email')) return false;
    const acc = accounts.find(a => linkedIds(s,'Account').includes(a.id));
    if (accFilter  && (!acc || acc.id !== accFilter)) return false;
    if (indFilter  && (!acc || F(acc,'Industry') !== indFilter)) return false;
    if (roleFilter && F(s,'Role') !== roleFilter) return false;
    return true;
  }).sort((a,b) => (F(a,'Name')||'').localeCompare(F(b,'Name')||'')), [stakeholders, accounts, accFilter, indFilter, roleFilter]);

  const allChecked = filtered.length > 0 && filtered.every(s => selected.has(s.id));

  // Enrollments of the active sequence
  const seqEnrollments = activeSeq?.enrollments || {};
  const enrolledIds = Object.keys(seqEnrollments);

  // Check if contact is actively enrolled in ANY other sequence
  const enrolledElsewhere = useMemo(() => {
    const map = {};
    sequences.forEach(seq => {
      if (seq.id === activeSeqId) return;
      Object.entries(seq.enrollments || {}).forEach(([stkId, en]) => {
        if (en.status === 'active') map[stkId] = seq.name;
      });
    });
    return map;
  }, [sequences, activeSeqId]);

  const pendingToEnroll = [...selected].map(id => stakeholders.find(s => s.id === id)).filter(Boolean)
    .filter(s => !seqEnrollments[s.id] || seqEnrollments[s.id].status === 'completed');

  // ── Monitor helpers ──
  const now = new Date();
  const isDue = (en) => en.status === 'active' && new Date(en.nextDateTime || en.nextDate || 0) <= now;
  const getNextDate = (en) => new Date(en.nextDateTime || en.nextDate || '9999');
  const groupDue       = enrolledIds.filter(id => isDue(seqEnrollments[id]));
  const groupScheduled = enrolledIds.filter(id => seqEnrollments[id].status === 'active' && !isDue(seqEnrollments[id])).sort((a,b) => getNextDate(seqEnrollments[a]) - getNextDate(seqEnrollments[b]));
  const groupFinished  = enrolledIds.filter(id => ['completed','replied'].includes(seqEnrollments[id].status));
  const pctDone = enrolledIds.length > 0 ? Math.round(groupFinished.length / enrolledIds.length * 100) : 0;

  const getDueLabel = (en) => {
    const dt = en.nextDateTime || en.nextDate;
    if (!dt) return { label: '—', color: 'var(--globant-muted)' };
    const diffD = Math.ceil((new Date(dt) - now) / 86400000);
    if (diffD <= 0) return { label: 'ahora', color: '#B4362C' };
    if (diffD === 1) return { label: 'mañana', color: '#9A6212' };
    if (diffD <= 3) return { label: `en ${diffD} días`, color: '#A0521A' };
    return { label: new Date(dt).toLocaleDateString('es', { month: 'short', day: 'numeric' }), color: 'var(--globant-muted)' };
  };

  // ── Enroll ──
  const enroll = async () => {
    if (!selectedCampaign || !activeSeq || (activeSeq.steps||[]).length === 0 || pendingToEnroll.length === 0) {
      window.__oikeToast?.('Elegí primero una secuencia y contactos.', 'warning'); return;
    }
    if (activeSeq.status === 'paused') {
      window.__oikeToast?.('Reanudá la secuencia antes de sumar contactos.', 'warning'); return;
    }
    setEnrolling(true);
    const today = new Date().toISOString().split('T')[0];
    const senderEmail = CURRENT_USER?.email || '';
    const cfg = activeSeq.config || {};
    const sendOnEnroll = cfg.sendOnEnroll !== false;
    const current = { ...(activeSeq.enrollments || {}) };
    let baseDateTime = enrollDT ? new Date(enrollDT) : new Date();
    if (!sendOnEnroll && (activeSeq.steps[0]?.waitDays || 0) > 0) baseDateTime.setDate(baseDateTime.getDate() + activeSeq.steps[0].waitDays);
    const nextDateTime = baseDateTime.toISOString();
    const nextDate = nextDateTime.split('T')[0];
    pendingToEnroll.forEach(s => { current[s.id] = { step: 0, nextDate, nextDateTime, status: 'active', senderEmail, enrolledDate: today }; });
    const newSeqs = updateActiveSeq({ enrollments: current });
    await saveSequences(newSeqs);
    window.__oikeToast?.(`${pendingToEnroll.length} ${pendingToEnroll.length>1?'contactos inscriptos':'contacto inscripto'}.`, 'success');
    setSelected(new Set());
    setEnrolling(false);
  };

  const unenroll = async (stkId) => {
    const current = { ...(activeSeq?.enrollments || {}) }; delete current[stkId];
    const newSeqs = updateActiveSeq({ enrollments: current });
    await saveSequences(newSeqs);
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const res = await fetch('/.netlify/functions/campaign-sequence-runner', { method: 'POST', headers: { Authorization: `Bearer ${AUTH_TOKEN}` } });
      let r = {}; try { r = await res.json(); } catch {}
      const s = r.sent ?? 0;
      if (s > 0) window.__oikeToast?.(`${s} ${s>1?'emails enviados':'email enviado'}.`, 'success');
      else window.__oikeToast?.('No hay emails para enviar ahora.', 'info');
      if (onLogActivity) onLogActivity();
    } catch(e) { window.__oikeToast?.('Falló el envío automático: ' + e.message, 'error'); }
    setRunning(false);
  };

  // ── AI Preview ──
  const generatePreview = async (stepIndex) => {
    if (generatingPreview !== null || !activeSeq) return;
    setGeneratingPreview(stepIndex);
    const step = activeSeq.steps[stepIndex];
    const ctx = ctxRecord;
    const campaignName = ctx ? (F(ctx,'Name') || F(ctx,'Event Name') || '') : '';
    const campaignType = ctx ? (F(ctx,'Type') || '') : '';
    const template     = ctx ? (F(ctx,'Message Template') || '') : '';
    const context      = ctx ? (F(ctx,'Context') || F(ctx,'Service | Solution Detail') || '') : '';
    const lang = activeSeq.language || 'es';
    const langInstruction = lang === 'es' ? 'Write in Spanish (Latin American, tuteo or voseo as natural).' : lang === 'pt' ? 'Write in Brazilian Portuguese.' : lang === 'fr' ? 'Write in French.' : 'Write in English.';
    const isFollowUp = stepIndex > 0;
    const prompt = `You are a senior B2B sales rep writing a ${step.channel || 'Email'} message for step ${stepIndex + 1} of the "${campaignName}" outreach sequence (${campaignType}).
LANGUAGE: ${langInstruction}
${isFollowUp ? `This is follow-up #${stepIndex+1} (sent ${step.waitDays} day${step.waitDays!==1?'s':''} after the previous touch${step.condition==='no_reply'?', only if no reply':''}). Reference that a previous message was sent and add a new angle. Keep it short — 3-4 sentences max.` : `This is the first touch. Keep it punchy and human — 4-5 sentences max. Open with a sharp observation, not a generic intro.`}
${step.note ? `Step intent: "${step.note}"` : ''}
${template ? `\nReference angle (DO NOT copy verbatim, rewrite naturally):\n"${template.slice(0,500)}"` : ''}
${context ? `\nContext:\n${context.slice(0,600)}` : ''}
Write for a fictional prospect — use {{first_name}} and {{company}} as tokens. Output ONLY the message body (no subject, no greeting, no signature). Sound human.`;
    try {
      const preview = await callOpenAI({ prompt, temperature: 0.72, max_tokens: 350, method: 'messages' });
      setStepPreviews(prev => ({ ...prev, [stepIndex]: preview }));
    } catch(e) { window.__oikeToast?.('No se pudo generar la vista previa: ' + e.message, 'error'); }
    setGeneratingPreview(null);
  };

  // ── Render helpers ──
  const sectionHead = (label) => (
    <div style={{ padding:'5px 14px', background:'rgba(30,42,44,0.02)', borderBottom:'1px solid var(--globant-border)' }}>
      <span style={{ fontSize:10, fontWeight:700, color:'var(--globant-muted)', textTransform:'uppercase', letterSpacing:0.8 }}>{label}</span>
    </div>
  );

  const renderMonitorRow = (stkId, showDivider) => {
    const en = seqEnrollments[stkId];
    const stk = stakeholders.find(s => s.id === stkId);
    if (!stk || !en) return null;
    const name = `${F(stk,'Name')}${F(stk,'Last name') ? ` ${F(stk,'Last name')}` : ''}`;
    const acc = accounts.find(a => linkedIds(stk,'Account').includes(a.id));
    const step = en.step || 0;
    const due = isDue(en);
    const dueInfo = en.status === 'active' ? getDueLabel(en) : null;
    const totalSteps = (activeSeq?.steps || []).length;
    let statusLine;
    if (en.status === 'completed') statusLine = <span style={{ color:'#1E7B4F', fontSize:12 }}>Terminada: se enviaron los {totalSteps} pasos</span>;
    else if (en.status === 'replied') statusLine = <span style={{ color:'#1E7B4F', fontSize:12 }}>Respondió: se detuvo</span>;
    else if (due) statusLine = <span style={{ color:'#B4362C', fontSize:12, fontWeight:600 }}>Paso {step+1} de {totalSteps}: enviar ahora</span>;
    else statusLine = <span style={{ fontSize:12, color:'var(--globant-muted)' }}>{step > 0 ? `${step} de ${totalSteps} enviados · ` : ''}<span style={{ color: dueInfo?.color }}>Próximo: paso {step+1} {dueInfo?.label}</span></span>;
    return (
      <div key={stkId} style={{ display:'flex', alignItems:'center', padding:'10px 14px', borderBottom: showDivider ? '1px solid var(--globant-border)' : 'none', gap:12 }}>
        <div style={{ flex:1 }}>
          <div style={{ fontWeight:600, fontSize:13, marginBottom:2 }}>{name}</div>
          <div style={{ display:'flex', gap:6, alignItems:'center' }}>
            {acc && <span style={{ fontSize:11, color:'var(--globant-muted)' }}>{F(acc,'Account Name')} ·</span>}
            {statusLine}
          </div>
        </div>
        <button type="button" onClick={() => unenroll(stkId)} aria-label={`Quitar a ${name} de la secuencia`} title="Quitar de la secuencia" style={{ background:'none', border:'none', color:'var(--globant-muted)', cursor:'pointer', fontSize:14 }}>✕</button>
      </div>
    );
  };

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:20 }}>

      {/* ── 1. Context ── */}
      <div style={{ ...CARD }}>
        <div style={{ fontSize:11, fontWeight:700, color:'var(--globant-muted)', textTransform:'uppercase', letterSpacing:1, marginBottom:12 }}>1 · Contexto</div>
        <div style={{ display:'flex', gap:8, marginBottom:12, flexWrap:'wrap' }}>
          {[['campaign','Campaña'],['event','Evento'],['service','Servicio']].map(([val,lbl]) => (
            <button key={val} type="button" aria-pressed={ctxType===val} onClick={() => setCtxType(val)} style={{ padding:'6px 14px', borderRadius:8, cursor:'pointer', fontSize:12, fontWeight:ctxType===val?700:400, background:ctxType===val?'var(--teal-dim)':'var(--globant-card)', color:ctxType===val?'#155C54':'var(--globant-muted)', border:`1px solid ${ctxType===val?'rgba(91,191,181,0.4)':'var(--globant-border)'}` }}>{lbl}</button>
          ))}
        </div>
        {ctxType === 'campaign' && (
          <select aria-label="Campaña" style={{ ...INPUT, maxWidth:340 }} value={campaignId} onChange={e => setCampaignId(e.target.value)}>
            <option value="">Elegí una campaña…</option>
            {campaigns.map(c => <option key={c.id} value={c.id}>{F(c,'Name')}</option>)}
          </select>
        )}
        {ctxType === 'event' && (
          <>
            <select aria-label="Evento" style={{ ...INPUT, maxWidth:340, marginBottom:8 }} value={eventId} onChange={e => setEventId(e.target.value)}>
              <option value="">Elegí un evento…</option>
              {events.map(e => <option key={e.id} value={e.id}>{F(e,'Event Name') || F(e,'Name') || e.id}</option>)}
            </select>
            <select aria-label="Campaña vinculada" style={{ ...INPUT, maxWidth:340, marginTop:4 }} value={campaignId} onChange={e => setCampaignId(e.target.value)}>
              <option value="">Vincular a una campaña (para el envío automático)…</option>
              {campaigns.map(c => <option key={c.id} value={c.id}>{F(c,'Name')}</option>)}
            </select>
          </>
        )}
        {ctxType === 'service' && (
          <>
            <select aria-label="Servicio" style={{ ...INPUT, maxWidth:340, marginBottom:8 }} value={serviceId} onChange={e => setServiceId(e.target.value)}>
              <option value="">Elegí un servicio…</option>
              {solutions.map(s => <option key={s.id} value={s.id}>{F(s,'Name')}</option>)}
            </select>
            <select aria-label="Campaña vinculada" style={{ ...INPUT, maxWidth:340, marginTop:4 }} value={campaignId} onChange={e => setCampaignId(e.target.value)}>
              <option value="">Vincular a una campaña (para el envío automático)…</option>
              {campaigns.map(c => <option key={c.id} value={c.id}>{F(c,'Name')}</option>)}
            </select>
          </>
        )}
        {ctxRecord && (
          <div style={{ marginTop:10, padding:'8px 12px', background:'rgba(91,191,181,0.06)', borderRadius:8, border:'1px solid rgba(91,191,181,0.2)', fontSize:12 }}>
            <strong>{F(ctxRecord,'Name') || F(ctxRecord,'Event Name') || '—'}</strong>
            {F(ctxRecord,'Type') && <span style={{ color:'var(--globant-muted)', marginLeft:8 }}>{F(ctxRecord,'Type')}</span>}
          </div>
        )}
      </div>

      {/* ── Analytics overview ── */}
      {selectedCampaign && seqAnalytics && seqAnalytics.total.sequences > 0 && (
        <div style={{ ...CARD }}>
          <div style={{ fontSize:11, fontWeight:700, color:'var(--globant-muted)', textTransform:'uppercase', letterSpacing:1, marginBottom:14 }}>Resumen</div>
          {/* Top KPIs */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(110px,1fr))', gap:10, marginBottom:16 }}>
            {[
              { label:'Secuencias',    value: seqAnalytics.total.sequences,           color:'var(--globant-text)' },
              { label:'Inscriptos',     value: seqAnalytics.total.enrolled,             color:'var(--globant-text)' },
              { label:'Emails enviados',  value: seqAnalytics.total.sent,                color:'var(--globant-text)' },
              { label:'Respuestas',      value: seqAnalytics.total.replied,             color:'#1E7B4F' },
              { label:'Tasa de respuesta',   value: seqAnalytics.total.replyRate + '%',     color: seqAnalytics.total.replyRate >= 20 ? '#1E7B4F' : seqAnalytics.total.replyRate >= 10 ? '#9A6212' : 'var(--globant-muted)' },
              { label:'Para enviar ahora',      value: seqAnalytics.total.due,                 color: seqAnalytics.total.due > 0 ? '#B4362C' : 'var(--globant-muted)' },
            ].map(({ label, value, color }) => (
              <div key={label} style={{ background:'rgba(30,42,44,0.03)', borderRadius:8, padding:'10px 12px', border:'1px solid var(--globant-border)' }}>
                <div style={{ fontSize:20, fontWeight:800, color, lineHeight:1.1 }}>{value}</div>
                <div style={{ fontSize:11, color:'var(--globant-muted)', marginTop:3 }}>{label}</div>
              </div>
            ))}
          </div>
          {/* Per-sequence table */}
          <div style={{ border:'1px solid var(--globant-border)', borderRadius:8, overflowX:'auto' }}>
            <div style={{ minWidth:540 }}>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 70px 70px 70px 70px 80px', gap:0, padding:'6px 12px', background:'rgba(30,42,44,0.04)', borderBottom:'1px solid var(--globant-border)' }}>
              {['Secuencia','Inscriptos','Enviados','Respuestas','% resp.','Pendientes'].map(h => (
                <span key={h} style={{ fontSize:10, fontWeight:700, color:'var(--globant-muted)', textTransform:'uppercase', letterSpacing:0.5 }}>{h}</span>
              ))}
            </div>
            {seqAnalytics.stats.map(({ seq, totalEnrolled, sentCount, repliedOut, replyRate, dueCount }) => {
              const meta = SEQ_STATUS_META[seq.status] || SEQ_STATUS_META.active;
              return (
                <div key={seq.id}
                  style={{ display:'grid', gridTemplateColumns:'1fr 70px 70px 70px 70px 80px', gap:0, padding:'9px 12px', borderBottom:'1px solid var(--globant-border)', cursor:'pointer', background: seq.id === activeSeqId ? 'rgba(91,191,181,0.05)' : 'transparent' }}
                  onClick={() => { setActiveSeqId(seq.id === activeSeqId ? null : seq.id); setSelected(new Set()); setStepPreviews({}); }}>
                  <div>
                    <span style={{ fontSize:13, fontWeight:600 }}>{seq.name}</span>
                    <span style={{ fontSize:10, color: meta.color, marginLeft:6, padding:'1px 6px', background: meta.bg, borderRadius:4, border:`1px solid ${meta.border}` }}>{meta.label}</span>
                  </div>
                  <span style={{ fontSize:13 }}>{totalEnrolled}</span>
                  <span style={{ fontSize:13 }}>{sentCount}</span>
                  <span style={{ fontSize:13, color: repliedOut > 0 ? '#1E7B4F' : 'var(--globant-muted)' }}>{repliedOut}</span>
                  <span style={{ fontSize:13, fontWeight:600, color: replyRate >= 20 ? '#1E7B4F' : replyRate >= 10 ? '#9A6212' : 'var(--globant-muted)' }}>{replyRate}%</span>
                  <span style={{ fontSize:13, color: dueCount > 0 ? '#B4362C' : 'var(--globant-muted)', fontWeight: dueCount > 0 ? 700 : 400 }}>{dueCount > 0 ? `${dueCount} ahora` : '—'}</span>
                </div>
              );
            })}
            </div>
          </div>
        </div>
      )}

      {/* ── 2. Sequences list ── */}
      {selectedCampaign && (
        <div style={{ ...CARD }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:8, flexWrap:'wrap', marginBottom:12 }}>
            <div style={{ fontSize:11, fontWeight:700, color:'var(--globant-muted)', textTransform:'uppercase', letterSpacing:1 }}>2 · Secuencias</div>
            <button className="action-btn" style={{ fontSize:12, padding:'5px 12px' }}
              onClick={() => {
                const seq = defaultSeq(`Secuencia ${sequences.length + 1}`);
                const newSeqs = [...sequences, seq];
                setSequences(newSeqs);
                setActiveSeqId(seq.id);
                setSelected(new Set());
                setStepPreviews({});
                saveSequences(newSeqs);
              }}>
              + Nueva secuencia
            </button>
          </div>
          {sequences.length === 0 && (
            <div style={{ textAlign:'center', padding:'24px 0', color:'var(--globant-muted)', fontSize:13 }}>
              Todavía no hay secuencias: creá la primera arriba.
            </div>
          )}
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            {sequences.map(seq => {
              const meta = SEQ_STATUS_META[seq.status] || SEQ_STATUS_META.active;
              const enrVals = Object.values(seq.enrollments || {});
              const enrollCount = enrVals.length;
              const activeCount = enrVals.filter(e => e.status === 'active').length;
              const dueCount = enrVals.filter(e => e.status === 'active' && new Date(e.nextDateTime || e.nextDate || 0) <= new Date()).length;
              const doneCount  = enrVals.filter(e => ['completed','replied'].includes(e.status)).length;
              const pct = enrollCount > 0 ? Math.round(doneCount / enrollCount * 100) : 0;
              const isActive = seq.id === activeSeqId;
              return (
                <div key={seq.id}
                  style={{ display:'flex', alignItems:'center', gap:12, flexWrap:'wrap', padding:'11px 14px', borderRadius:8, cursor:'pointer', border:`1px solid ${isActive ? 'var(--globant-green)' : 'var(--globant-border)'}`, background: isActive ? 'var(--teal-dim)' : 'var(--globant-card)', transition:'all 0.15s' }}
                  onClick={() => { setActiveSeqId(seq.id === activeSeqId ? null : seq.id); setSelected(new Set()); setStepPreviews({}); }}>
                  {/* Name / rename */}
                  <div style={{ flex:1, minWidth:0 }}>
                    {renamingId === seq.id ? (
                      <input autoFocus value={renameVal} onChange={e => setRenameVal(e.target.value)}
                        onBlur={() => { const newSeqs = sequences.map(s => s.id===seq.id ? {...s,name:renameVal||s.name} : s); setSequences(newSeqs); saveSequences(newSeqs); setRenamingId(null); }}
                        onKeyDown={e => { if (e.key==='Enter'||e.key==='Escape') e.target.blur(); }}
                        onClick={e => e.stopPropagation()}
                        style={{ ...INPUT, fontSize:13, fontWeight:600, padding:'3px 6px', width:'100%' }} />
                    ) : (
                      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                        <span style={{ fontSize:13, fontWeight:600, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{seq.name}</span>
                        <button onClick={e => { e.stopPropagation(); setRenamingId(seq.id); setRenameVal(seq.name); }}
                          style={{ background:'none', border:'none', color:'var(--globant-muted)', cursor:'pointer', fontSize:11, padding:'0 2px', opacity:0.6 }} title="Cambiar nombre">✏️</button>
                      </div>
                    )}
                    <div style={{ display:'flex', gap:8, alignItems:'center', marginTop:3, flexWrap:'wrap' }}>
                      <span style={{ fontSize:11, color: meta.color }}>{meta.label}</span>
                      <span style={{ fontSize:11, color:'var(--globant-muted)' }}>· {(seq.steps||[]).length} pasos</span>
                      {enrollCount > 0 && <>
                        <span style={{ fontSize:11, color:'var(--globant-muted)' }}>· {enrollCount} inscriptos</span>
                        {dueCount > 0 && <span style={{ fontSize:11, color:'#B4362C', fontWeight:600 }}>· {dueCount} para enviar ahora</span>}
                        {activeCount > 0 && dueCount === 0 && <span style={{ fontSize:11, color:'#9A6212' }}>· {activeCount} programados</span>}
                        {doneCount > 0 && <span style={{ fontSize:11, color:'#1E7B4F' }}>· {doneCount} terminados ({pct}%)</span>}
                      </>}
                    </div>
                    {enrollCount > 0 && (
                      <div style={{ marginTop:5, height:3, borderRadius:2, background:'var(--globant-border)', overflow:'hidden', maxWidth:200 }}>
                        <div style={{ height:'100%', width:`${pct}%`, background:'#1E7B4F', borderRadius:2, transition:'width 0.3s' }} />
                      </div>
                    )}
                  </div>
                  {/* Status toggle */}
                  <div style={{ display:'flex', gap:4, flexWrap:'wrap' }} onClick={e => e.stopPropagation()}>
                    {(['active','paused','finished']).map(st => {
                      const m = SEQ_STATUS_META[st];
                      const isSelected = seq.status === st;
                      return (
                        <button key={st} onClick={() => {
                          const newSeqs = sequences.map(s => s.id===seq.id ? {...s,status:st} : s);
                          setSequences(newSeqs);
                          saveSequences(newSeqs);
                        }} style={{ fontSize:10, padding:'3px 8px', borderRadius:6, cursor:'pointer', fontWeight:isSelected?700:400, background:isSelected?m.bg:'transparent', color:isSelected?m.color:'var(--globant-muted)', border:`1px solid ${isSelected?m.border:'var(--globant-border)'}` }}>
                          {m.label}
                        </button>
                      );
                    })}
                    <button onClick={() => { if (!window.confirm('¿Borrar esta secuencia?')) return; const newSeqs = sequences.filter(s => s.id!==seq.id); setSequences(newSeqs); if (activeSeqId===seq.id) setActiveSeqId(null); saveSequences(newSeqs); }}
                      aria-label="Borrar secuencia" title="Borrar secuencia" style={{ background:'none', border:'none', color:'var(--globant-muted)', cursor:'pointer', fontSize:12, padding:'2px 4px' }}>🗑</button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Sequence editor (shown when a sequence is selected) ── */}
      {activeSeq && (
        <>
          {/* ── 3. Steps ── */}
          <div style={{ ...CARD }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:8, flexWrap:'wrap', marginBottom:12 }}>
              <div style={{ fontSize:11, fontWeight:700, color:'var(--globant-muted)', textTransform:'uppercase', letterSpacing:1 }}>3 · Pasos de la secuencia</div>
              <div style={{ display:'flex', alignItems:'center', gap:6, flexWrap:'wrap' }}>
                <span style={{ fontSize:11, color:'var(--globant-muted)' }}>Idioma:</span>
                {[['es','🇦🇷 ES'],['en','🇺🇸 EN'],['pt','🇧🇷 PT'],['fr','🇫🇷 FR']].map(([val,lbl]) => (
                  <button key={val} onClick={() => { updateActiveSeq({language:val}); setStepPreviews({}); }} style={{ padding:'3px 10px', borderRadius:6, cursor:'pointer', fontSize:11, fontWeight:(activeSeq.language||'es')===val?700:400, background:(activeSeq.language||'es')===val?'rgba(96,165,250,0.15)':'rgba(30,42,44,0.04)', color:(activeSeq.language||'es')===val?'#2D5F99':'var(--globant-muted)', border:`1px solid ${(activeSeq.language||'es')===val?'rgba(96,165,250,0.4)':'var(--globant-border)'}` }}>{lbl}</button>
                ))}
              </div>
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
              {(activeSeq.steps||[]).map((step, i) => {
                const chColor = step.channel === 'Email' ? '#2D5F99' : step.channel === 'LinkedIn' ? '#6366f1' : '#1E7B4F';
                const chBg = step.channel === 'Email' ? 'rgba(96,165,250,0.15)' : step.channel === 'LinkedIn' ? 'rgba(99,102,241,0.15)' : 'rgba(74,222,128,0.15)';
                const chIcon = step.channel === 'Email' ? '✉️' : step.channel === 'LinkedIn' ? '🔗' : '💬';
                return (
                  <div key={i} style={{ display:'flex', gap:0 }}>
                    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', width:32, flexShrink:0 }}>
                      <div style={{ width:26, height:26, borderRadius:'50%', background:chBg, border:`2px solid ${chColor}`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, zIndex:1 }}>{chIcon}</div>
                      {i < activeSeq.steps.length-1 && <div style={{ width:2, flex:1, minHeight:8, background:'var(--globant-border)', margin:'2px 0' }} />}
                    </div>
                    <div style={{ flex:1, marginBottom:i<activeSeq.steps.length-1?8:0, marginLeft:10, padding:'8px 12px', borderRadius:8, background:'rgba(30,42,44,0.04)', border:'1px solid var(--globant-border)' }}>
                      <div style={{ display:'flex', gap:6, alignItems:'center', flexWrap:'wrap' }}>
                        <span style={{ fontSize:10, fontWeight:700, color:'var(--globant-muted)', minWidth:18 }}>#{i+1}</span>
                        {i === 0
                          ? <span style={{ fontSize:11, color:'var(--globant-muted)', background:'rgba(167,139,250,0.1)', padding:'2px 8px', borderRadius:6 }}>Día 0: primer contacto</span>
                          : <div style={{ display:'flex', alignItems:'center', gap:4, fontSize:11, color:'var(--globant-muted)' }}>
                              Esperar <input type="number" min={1} value={step.waitDays} onChange={e => { const ns = activeSeq.steps.map((s,idx)=>idx===i?{...s,waitDays:parseInt(e.target.value)||1}:s); updateActiveSeq({steps:ns}); }} style={{ width:40, textAlign:'center', background:'rgba(30,42,44,0.06)', border:'1px solid var(--globant-border)', borderRadius:4, color:'var(--globant-text)', fontSize:11, padding:'1px 4px' }} /> días
                            </div>
                        }
                        <select value={step.channel} onChange={e => { const ns = activeSeq.steps.map((s,idx)=>idx===i?{...s,channel:e.target.value}:s); updateActiveSeq({steps:ns}); setStepPreviews(p => { const n={...p}; delete n[i]; return n; }); }} style={{ ...SEL, fontSize:11, padding:'3px 6px', minWidth:88 }}>
                          <option>Email</option><option>LinkedIn</option><option>WhatsApp</option>
                        </select>
{i > 0 && (
                          <select value={step.condition} onChange={e => { const ns = activeSeq.steps.map((s,idx)=>idx===i?{...s,condition:e.target.value}:s); updateActiveSeq({steps:ns}); }} style={{ ...SEL, fontSize:11, padding:'3px 6px', minWidth:130 }}>
                            <option value="no_reply">Solo si no respondió</option>
                            <option value="always">Enviar siempre</option>
                          </select>
                        )}
                        <input value={step.note||''} onChange={e => { const ns = activeSeq.steps.map((s,idx)=>idx===i?{...s,note:e.target.value}:s); updateActiveSeq({steps:ns}); }} placeholder="Nota del paso (opcional)" style={{ ...INPUT, flex:1, minWidth:100, fontSize:11, padding:'3px 6px' }} />
                        <button onClick={() => generatePreview(i)} disabled={generatingPreview !== null} style={{ background:'rgba(167,139,250,0.12)', border:'1px solid rgba(167,139,250,0.3)', color:'#6B5CA5', borderRadius:6, cursor:'pointer', fontSize:11, padding:'3px 8px', whiteSpace:'nowrap', flexShrink:0 }}>
                          {generatingPreview === i ? '…' : 'Vista previa'}
                        </button>
                        <button onClick={() => { const ns = activeSeq.steps.filter((_,idx)=>idx!==i); updateActiveSeq({steps:ns}); setStepPreviews(p=>{const n={...p};delete n[i];return n;}); }} style={{ background:'none', border:'none', color:'var(--globant-muted)', cursor:'pointer', fontSize:13, padding:'0 2px' }}>✕</button>
                      </div>
                      {stepPreviews[i] && (
                        <div style={{ marginTop:8, padding:'10px 12px', background:'rgba(167,139,250,0.07)', borderRadius:6, border:'1px solid rgba(167,139,250,0.2)', position:'relative' }}>
                          <div style={{ fontSize:10, fontWeight:700, color:'#6B5CA5', textTransform:'uppercase', letterSpacing:0.8, marginBottom:6 }}>Vista previa con IA: paso {i+1}</div>
                          <div style={{ fontSize:12, color:'var(--globant-text)', lineHeight:1.6, whiteSpace:'pre-wrap' }}>{stepPreviews[i]}</div>
                          <button onClick={() => { setStepPreviews(p=>{const n={...p};delete n[i];return n;}); generatePreview(i); }} disabled={generatingPreview!==null} style={{ position:'absolute', top:8, right:8, background:'none', border:'none', color:'var(--globant-muted)', cursor:'pointer', fontSize:11 }}>↻</button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{ display:'flex', gap:8, marginTop:8 }}>
              <button className="action-btn" style={{ fontSize:12, background:'rgba(30,42,44,0.05)', border:'1px solid var(--globant-border)', color:'var(--globant-muted)' }}
                onClick={() => { const last = activeSeq.steps[activeSeq.steps.length-1]; updateActiveSeq({steps:[...activeSeq.steps, { channel:'Email', waitDays: last ? last.waitDays+3 : 3, condition:'no_reply', note:'' }]}); }}>
                + Agregar paso
              </button>
              <button className="action-btn" style={{ fontSize:12 }} onClick={() => saveSequences(sequences)} disabled={saving}>
                {saving ? 'Guardando…' : 'Guardar pasos'}
              </button>
            </div>
          </div>

          {/* ── 4. Settings ── */}
          <div style={{ ...CARD }}>
            <div style={{ fontSize:11, fontWeight:700, color:'var(--globant-muted)', textTransform:'uppercase', letterSpacing:1, marginBottom:12 }}>4 · Configuración</div>
            <div style={{ display:'flex', gap:16, flexWrap:'wrap' }}>
              <div>
                <div style={{ ...FIELD_LABEL, marginBottom:6 }}>Modo de envío</div>
                <div style={{ display:'flex', gap:6 }}>
                  {[['send','Enviar solo'],['draft','Guardar como borrador']].map(([val,lbl]) => (
                    <button key={val} onClick={() => updateActiveSeq({config:{...(activeSeq.config||{}),sendMode:val}})} style={{ padding:'5px 12px', borderRadius:8, cursor:'pointer', fontSize:12, fontWeight:(activeSeq.config?.sendMode||'send')===val?700:400, background:(activeSeq.config?.sendMode||'send')===val?'var(--teal-dim)':'var(--globant-card)', color:(activeSeq.config?.sendMode||'send')===val?'#155C54':'var(--globant-muted)', border:`1px solid ${(activeSeq.config?.sendMode||'send')===val?'rgba(91,191,181,0.4)':'var(--globant-border)'}` }}>{lbl}</button>
                  ))}
                </div>
              </div>
              <div>
                <div style={{ ...FIELD_LABEL, marginBottom:6 }}>Primer email</div>
                <div style={{ display:'flex', gap:6 }}>
                  {[[true,'En el momento'],[false,'Programar']].map(([val,lbl]) => {
                    const cur = activeSeq.config?.sendOnEnroll !== false;
                    return (
                      <button key={String(val)} onClick={() => {
                        updateActiveSeq({config:{...(activeSeq.config||{}),sendOnEnroll:val}});
                        if (val) setEnrollDT('');
                        else { const d = new Date(); d.setMinutes(0,0,0); d.setHours(d.getHours()+1); setEnrollDT(d.toISOString().slice(0,16)); }
                      }} style={{ padding:'5px 12px', borderRadius:8, cursor:'pointer', fontSize:12, fontWeight:cur===val?700:400, background:cur===val?'rgba(167,139,250,0.15)':'rgba(30,42,44,0.04)', color:cur===val?'#6B5CA5':'var(--globant-muted)', border:`1px solid ${cur===val?'rgba(167,139,250,0.4)':'var(--globant-border)'}` }}>{lbl}</button>
                    );
                  })}
                </div>
                {activeSeq.config?.sendOnEnroll === false && <input type="datetime-local" style={{ ...INPUT, marginTop:8, maxWidth:220 }} value={enrollDT} onChange={e => setEnrollDT(e.target.value)} />}
              </div>
              <div>
                <div style={{ ...FIELD_LABEL, marginBottom:6 }}>Hora de los seguimientos</div>
                <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
                  <select aria-label="Hora de envío" style={{ ...SEL }} value={activeSeq.config?.sendHour ?? 9} onChange={e => updateActiveSeq({config:{...(activeSeq.config||{}),sendHour:parseInt(e.target.value)}})}>
                    {Array.from({length:24},(_,h)=><option key={h} value={h}>{String(h).padStart(2,'0')}:00</option>)}
                  </select>
                  <select aria-label="Zona horaria" style={{ ...SEL, minWidth:200 }} value={activeSeq.config?.timezone || 'America/Argentina/Buenos_Aires'} onChange={e => updateActiveSeq({config:{...(activeSeq.config||{}),timezone:e.target.value}})}>
                    {SEQ_TIMEZONES.map(tz => <option key={tz.value} value={tz.value}>{tz.label}</option>)}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* ── 5. Contacts ── */}
          <div style={{ ...CARD }}>
            <div style={{ fontSize:11, fontWeight:700, color:'var(--globant-muted)', textTransform:'uppercase', letterSpacing:1, marginBottom:12 }}>5 · Contactos a inscribir</div>
            <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:10 }}>
              <select aria-label="Empresa" style={{ ...SEL }} value={accFilter} onChange={e => { setAccFilter(e.target.value); setSelected(new Set()); }}>
                <option value="">Todas las empresas</option>
                {uniqueAccounts.map(a => <option key={a.id} value={a.id}>{F(a,'Account Name')}</option>)}
              </select>
              <select aria-label="Industria" style={{ ...SEL }} value={indFilter} onChange={e => { setIndFilter(e.target.value); setSelected(new Set()); }}>
                <option value="">Todas las industrias</option>
                {industries.map(i => <option key={i}>{i}</option>)}
              </select>
              <select aria-label="Rol" style={{ ...SEL }} value={roleFilter} onChange={e => { setRoleFilter(e.target.value); setSelected(new Set()); }}>
                <option value="">Todos los roles</option>
                {roles.map(r => <option key={r}>{r}</option>)}
              </select>
              <span style={{ ...MUTED, alignSelf:'center' }}>{filtered.length} {filtered.length === 1 ? 'contacto' : 'contactos'} con email</span>
            </div>
            <div style={{ border:'1px solid var(--globant-border)', borderRadius:8, overflow:'hidden', maxHeight:260, overflowY:'auto' }}>
              <div style={{ display:'flex', alignItems:'center', gap:8, padding:'7px 12px', background:'rgba(30,42,44,0.04)', borderBottom:'1px solid var(--globant-border)' }}>
                <input type="checkbox" aria-label="Elegir todos" checked={allChecked} onChange={e => setSelected(e.target.checked ? new Set(filtered.map(s=>s.id)) : new Set())} />
                <span style={{ fontSize:12, color:'var(--globant-muted)' }}>Elegir todos ({filtered.length})</span>
                {selected.size > 0 && <span style={{ ...CHIP, marginLeft:'auto' }}>{selected.size} elegidos</span>}
              </div>
              {filtered.map(s => {
                const acc = accounts.find(a => linkedIds(s,'Account').includes(a.id));
                const isChecked = selected.has(s.id);
                const inThisSeq = seqEnrollments[s.id] && seqEnrollments[s.id].status !== 'completed';
                const inOther = enrolledElsewhere[s.id];
                return (
                  <div key={s.id} onClick={() => { if (inThisSeq || inOther) return; const n = new Set(selected); n.has(s.id) ? n.delete(s.id) : n.add(s.id); setSelected(n); }}
                    style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 12px', borderBottom:'1px solid var(--globant-border)', cursor: (inThisSeq||inOther) ? 'default' : 'pointer', background: isChecked ? 'var(--teal-dim)' : 'transparent', opacity: (inThisSeq||inOther) ? 0.5 : 1 }}>
                    <input type="checkbox" checked={isChecked} readOnly style={{ pointerEvents:'none', flexShrink:0 }} />
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:13, fontWeight:500 }}>{F(s,'Name')}{F(s,'Last name') ? ` ${F(s,'Last name')}` : ''}</div>
                      <div style={{ fontSize:11, color:'var(--globant-muted)' }}>{F(s,'Role')}{acc ? ` · ${F(acc,'Account Name')}` : ''}</div>
                    </div>
                    {inThisSeq && <span style={{ fontSize:10, color:'var(--globant-muted)', fontStyle:'italic' }}>en esta secuencia</span>}
                    {!inThisSeq && inOther && <span style={{ fontSize:10, color:'#9A6212', fontStyle:'italic' }}>en «{inOther}»</span>}
                  </div>
                );
              })}
              {filtered.length === 0 && <div style={{ padding:20, textAlign:'center', color:'var(--globant-muted)', fontSize:12 }}>Ningún contacto coincide con los filtros</div>}
            </div>
          </div>

          {/* ── 6. Enroll ── */}
          <div style={{ display:'flex', alignItems:'center', gap:12, flexWrap:'wrap', padding:'14px 16px', background:'var(--teal-dim)', borderRadius:10, border:'1px solid rgba(31,122,112,0.25)' }}>
            <div style={{ flex:1, minWidth:200 }}>
              {activeSeq.status === 'paused'
                ? <span style={{ fontSize:13, color:'#9A6212' }}>La secuencia está en pausa: reanudala para sumar contactos</span>
                : pendingToEnroll.length === 0
                ? <span style={{ fontSize:13, color:'var(--globant-muted)' }}>Elegí contactos para inscribir en «{activeSeq.name}»</span>
                : <span style={{ fontSize:13, color:'var(--globant-text)', fontWeight:600 }}>{pendingToEnroll.length} {pendingToEnroll.length>1?'contactos listos':'contacto listo'} para inscribir</span>
              }
            </div>
            <button className="action-btn btn-primary" style={{ fontSize:13, padding:'9px 20px' }}
              disabled={activeSeq.status==='paused' || pendingToEnroll.length===0 || enrolling || (activeSeq.steps||[]).length===0}
              onClick={enroll}>
              {enrolling ? 'Inscribiendo…' : `Inscribir ${pendingToEnroll.length > 0 ? pendingToEnroll.length : ''} →`}
            </button>
          </div>

          {/* ── 7. Monitor + Sent log ── */}
          {enrolledIds.length > 0 && (
            <div style={{ ...CARD, padding:0, overflow:'hidden' }}>
              {/* Header */}
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:8, flexWrap:'wrap', padding:'12px 16px', borderBottom:'1px solid var(--globant-border)' }}>
                <div>
                  <span style={{ fontWeight:700, fontSize:13 }}>{activeSeq.name}</span>
                  <span style={{ fontSize:12, color:'var(--globant-muted)', marginLeft:8 }}>{enrolledIds.length} inscriptos · {pctDone}% terminado</span>
                </div>
                <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                  <span style={{ fontSize:11, color:'#1E7B4F' }}>Envío automático cada hora</span>
                  <button className="action-btn btn-ghost" style={{ fontSize:11 }} onClick={runNow} disabled={running}>
                    {running ? '…' : 'Ejecutar ahora'}
                  </button>
                </div>
              </div>
              {/* Progress bar */}
              <div style={{ height:3, background:'rgba(30,42,44,0.07)' }}>
                <div style={{ height:'100%', width:`${pctDone}%`, background:'linear-gradient(90deg,#1E7B4F,#2D5F99)', transition:'width 0.3s' }} />
              </div>
              {groupDue.length > 0 && (
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 14px', background:'rgba(239,68,68,0.07)', borderBottom:'1px solid var(--globant-border)' }}>
                  <span style={{ fontSize:12, fontWeight:600, color:'#B4362C' }}>{groupDue.length} {groupDue.length>1?'emails listos':'email listo'} para enviar</span>
                  <button className="action-btn btn-primary" style={{ fontSize:12 }} onClick={runNow} disabled={running}>Enviar ahora</button>
                </div>
              )}
              {/* Pipeline view */}
              <div style={{ maxHeight:320, overflowY:'auto' }}>
                {groupDue.length > 0 && (<>{sectionHead('Listos para enviar')}{groupDue.map((id,i)=>renderMonitorRow(id, i<groupDue.length-1||groupScheduled.length>0||groupFinished.length>0))}</>)}
                {groupScheduled.length > 0 && (<>{sectionHead('Programados')}{groupScheduled.map((id,i)=>renderMonitorRow(id, i<groupScheduled.length-1||groupFinished.length>0))}</>)}
                {groupFinished.length > 0 && (<>{sectionHead('Terminados')}{groupFinished.map((id,i)=>renderMonitorRow(id, i<groupFinished.length-1))}</>)}
              </div>

              {/* ── Sent messages log ── */}
              {(() => {
                const sentLog = outreach
                  .filter(o => {
                    const stkIds = linkedIds(o,'Stakeholder');
                    return stkIds.some(id => enrolledIds.includes(id));
                  })
                  .sort((a,b) => (F(b,'Date')||'').localeCompare(F(a,'Date')||''));
                if (!sentLog.length) return null;
                return (
                  <>
                    <div style={{ padding:'8px 14px', background:'rgba(30,42,44,0.02)', borderTop:'1px solid var(--globant-border)', borderBottom:'1px solid var(--globant-border)' }}>
                      <span style={{ fontSize:10, fontWeight:700, color:'var(--globant-muted)', textTransform:'uppercase', letterSpacing:0.8 }}>Mensajes enviados ({sentLog.length})</span>
                    </div>
                    <div style={{ maxHeight:400, overflowY:'auto' }}>
                      {sentLog.map((o, idx) => {
                        const stkId = linkedIds(o,'Stakeholder')[0];
                        const stk = stakeholders.find(s => s.id === stkId);
                        const name = stk ? `${F(stk,'Name')}${F(stk,'Last name') ? ` ${F(stk,'Last name')}` : ''}` : 'Sin nombre';
                        const acc = stk ? accounts.find(a => linkedIds(stk,'Account').includes(a.id)) : null;
                        const status = F(o,'Status') || 'Sent';
                        const date = F(o,'Date') || '';
                        const notes = F(o,'Notes') || '';
                        const subject = notes.replace(/^\[gmsg:[^\]]+\]\s*/,'');
                        const message = F(o,'Message') || '';
                        const isDraft = status === 'Draft';
                        const isReplied = ['received','replied'].includes(status.toLowerCase());
                        const statusColor = isReplied ? '#1E7B4F' : isDraft ? '#6B5CA5' : 'var(--globant-muted)';
                        const expanded = expandedLog.has(o.id);
                        const toggleExpand = () => setExpandedLog(prev => { const n = new Set(prev); n.has(o.id) ? n.delete(o.id) : n.add(o.id); return n; });
                        return (
                          <div key={o.id} style={{ borderBottom: idx < sentLog.length-1 ? '1px solid var(--globant-border)' : 'none' }}>
                            <div onClick={toggleExpand} style={{ display:'flex', alignItems:'flex-start', gap:12, padding:'10px 14px', cursor:'pointer' }}>
                              <div style={{ flex:1, minWidth:0 }}>
                                <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                                  <span style={{ fontSize:13, fontWeight:600 }}>{name}</span>
                                  {acc && <span style={{ fontSize:11, color:'var(--globant-muted)' }}>{F(acc,'Account Name')}</span>}
                                  <span style={{ fontSize:11, padding:'1px 7px', borderRadius:10, background: isReplied ? 'rgba(74,222,128,0.12)' : isDraft ? 'rgba(167,139,250,0.12)' : 'rgba(30,42,44,0.06)', color: statusColor, border:`1px solid ${isReplied ? 'rgba(74,222,128,0.3)' : isDraft ? 'rgba(167,139,250,0.3)' : 'var(--globant-border)'}` }}>
                                    {isReplied ? 'Respondió' : isDraft ? 'Borrador' : 'Enviado'}
                                  </span>
                                  {date && <span style={{ fontSize:11, color:'var(--globant-muted)' }}>{date}</span>}
                                </div>
                                {subject && <div style={{ fontSize:12, color:'var(--globant-muted)', marginTop:2, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>Asunto: {subject}</div>}
                              </div>
                              <span style={{ fontSize:12, color:'var(--globant-muted)', flexShrink:0 }}>{expanded ? '▲' : '▼'}</span>
                            </div>
                            {expanded && message && (
                              <div style={{ padding:'0 14px 12px 14px' }}>
                                <div style={{ fontSize:12, color:'var(--globant-text)', lineHeight:1.6, whiteSpace:'pre-wrap', background:'rgba(30,42,44,0.03)', borderRadius:6, padding:'10px 12px', border:'1px solid var(--globant-border)' }}>
                                  {message}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </>
                );
              })()}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function MessageLab({ data, api, onLogActivity, onUpdateRecord, navigateToStakeholderId, clearNavigateStakeholder }) {
  const [tab, setTab] = useState('individual');
  const tabs = [
    { key: 'individual', label: 'Individual' },
    { key: 'bulk',       label: 'En lote' },
    { key: 'sequences',  label: 'Secuencias' },
  ];

  return (
    <div>
      <div className="page-header">
        <div style={{ minWidth: 0 }}>
          <h1>Prospección y mensajes</h1>
          <p>Prepará mensajes con IA usando tus prompts de Configuración, el servicio, la campaña y el historial de cada contacto. Todo queda como borrador hasta que lo revisás y elegís un canal.</p>
        </div>
      </div>
      <div className="tabs" role="tablist" aria-label="Prospección y mensajes" style={{ overflowX: 'auto' }}>
        {tabs.map(t => (
          <button key={t.key} type="button" role="tab" aria-selected={tab === t.key} className={tab === t.key ? 'active' : ''}
            onClick={() => setTab(t.key)}>{t.label}</button>
        ))}
      </div>
      <div className="message-lab-body" style={{ padding: '16px clamp(16px, 3vw, 20px) 40px', maxWidth: 1000, boxSizing: 'border-box' }}>
        <div role="tabpanel" hidden={tab !== 'individual'}><IndividualTab data={data} preselectId={navigateToStakeholderId} clearPreselectId={clearNavigateStakeholder} /></div>
        <div role="tabpanel" hidden={tab !== 'bulk'}><BulkTab data={data} /></div>
        <div role="tabpanel" hidden={tab !== 'sequences'}><SequencesTab data={data} api={api} onLogActivity={onLogActivity} onUpdateRecord={onUpdateRecord} /></div>
      </div>
    </div>
  );
}
